import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card, Table, Alert, Spinner, Button, Badge } from 'react-bootstrap';
import { fetchWithAuth } from '../lib/supabaseClient';

// Formatted in London time, not the device's - the same reason the
// voucher clock is: a phone set to another timezone would otherwise
// report the wrong day.
const dateTimeFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const monthFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  month: 'long',
  year: 'numeric',
});

interface MonthlyRow {
  month: string;
  vouchers_issued: number;
  attempts_rejected: number;
  distinct_names: number;
  uses: number;
}

interface Entry {
  name: string;
  created_at: string;
  name_known: boolean;
  ref_code: string;
}

interface Report {
  role: 'admin' | 'viewer';
  monthly: MonthlyRow[];
  totals: {
    vouchersIssued: number;
    attemptsRejected: number;
    uses: number;
    distinctPeople: number;
  };
  entries?: Entry[];
  page?: number;
  pageSize: number;
  totalEntries?: number;
}

const StatCard: React.FC<{ label: string; value: number; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <Col xs={6} lg={3}>
    <Card className="h-100 text-center">
      <Card.Body>
        <div className="report-stat">{value.toLocaleString('en-GB')}</div>
        <div className="report-stat-label">{label}</div>
        {hint && <div className="text-muted small mt-1">{hint}</div>}
      </Card.Body>
    </Card>
  </Col>
);

const DiscountReport: React.FC = () => {
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const load = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(
        `/.netlify/functions/discount-report?page=${targetPage}`
      );
      if (response.status === 403) {
        throw new Error(
          "This report is limited to club officials. If you think you should have access, ask Andrew to add your email address."
        );
      }
      if (response.status === 401) {
        throw new Error('Your session has expired. Please sign in again.');
      }
      if (!response.ok) throw new Error('Could not load the report.');
      setReport(await response.json());
    } catch (err: any) {
      setError(err?.message || 'Could not load the report.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(page);
  }, [load, page]);

  const head = (
    <Helmet>
      <title>Discount Usage | Ashridge Park FC</title>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );

  if (loading && !report) {
    return (
      <>
        {head}
        <Container className="p-5 my-5 text-center">
          <Spinner animation="border">
            <span className="visually-hidden">Loading the report</span>
          </Spinner>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        {head}
        <Container className="p-3 my-4" style={{ maxWidth: 640 }}>
          <h1 className="mb-4 page-title-below-logo">Discount Usage</h1>
          <Alert variant="warning">{error}</Alert>
        </Container>
      </>
    );
  }

  if (!report) return head;

  const totalPages = report.totalEntries
    ? Math.ceil(report.totalEntries / report.pageSize)
    : 0;

  return (
    <>
      {head}
      <Container className="p-3 my-4">
        <h1 className="mb-2 page-title-below-logo">Discount Usage</h1>
        <p className="text-muted mb-4">
          Salty Olive 10% discount, from the Ashridge Park members' page.
        </p>

        <Row className="g-3 mb-4">
          <StatCard
            label="Times used"
            value={report.totals.uses}
            hint="One person, one day"
          />
          <StatCard label="People" value={report.totals.distinctPeople} />
          <StatCard
            label="Vouchers shown"
            value={report.totals.vouchersIssued}
            hint="Includes repeat views"
          />
          <StatCard
            label="Turned away"
            value={report.totals.attemptsRejected}
            hint="Failed the name check"
          />
        </Row>

        <h2 className="h5 mb-3">By month</h2>
        <div className="table-responsive mb-4">
          <Table striped bordered hover className="custom-fixture-table">
            <thead>
              <tr>
                <th>Month</th>
                <th className="text-end">Times used</th>
                <th className="text-end">People</th>
                <th className="text-end">Vouchers shown</th>
                <th className="text-end">Turned away</th>
              </tr>
            </thead>
            <tbody>
              {report.monthly.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    Nothing yet.
                  </td>
                </tr>
              ) : (
                report.monthly.map((row) => (
                  <tr key={row.month}>
                    <td>{monthFormat.format(new Date(`${row.month}T12:00:00Z`))}</td>
                    <td className="text-end">{row.uses}</td>
                    <td className="text-end">{row.distinct_names}</td>
                    <td className="text-end">{row.vouchers_issued}</td>
                    <td className="text-end">{row.attempts_rejected}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {report.role === 'viewer' && (
          <Alert variant="light" className="border">
            These are totals only. Individual names stay with the club.
          </Alert>
        )}

        {report.role === 'admin' && report.entries && (
          <>
            <h2 className="h5 mb-3">Who and when</h2>
            <div className="table-responsive">
              <Table striped bordered hover className="custom-fixture-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Date &amp; time</th>
                    <th>Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {report.entries.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center text-muted">
                        No vouchers yet.
                      </td>
                    </tr>
                  ) : (
                    report.entries.map((entry) => (
                      <tr key={entry.ref_code + entry.created_at}>
                        <td>
                          {entry.name}{' '}
                          {entry.name_known ? (
                            <Badge bg="success" className="ms-1">
                              Known
                            </Badge>
                          ) : (
                            <Badge bg="secondary" className="ms-1">
                              Not on our list
                            </Badge>
                          )}
                        </td>
                        <td>{dateTimeFormat.format(new Date(entry.created_at))}</td>
                        <td className="text-muted small">{entry.ref_code}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center">
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={page === 0 || loading}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-muted small">
                  Page {page + 1} of {totalPages} &middot; {report.totalEntries} vouchers
                </span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={page + 1 >= totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default DiscountReport;
